import { PluginRetrievalUtils } from "./PluginRetrievalUtils";
import { GridCell } from "../components/cell/grid-cell";
import { VIEW_COLORS } from "../const/view-colors.const";
import { CellRetrievalUtils } from "./CellRetrievalUtils";
import { getParentSize } from "../../../diagram/diagram-assets/diagram-positioning-state";
import { CellIndependentPluginHandler } from "../modules/cellIndependentHandlers/CellIndependentPluginHandler";
import { AnchoredPluginsHandler } from "../modules/AnchoredPluginsHandler";

/**
 * @Description - grid drawing utils, mostly called from gridView
 */
export class GridDrawingUtils {
    cellRetrieval = new CellRetrievalUtils();

    /**
     * @description Draws all the cells that are in the viewport
     * @param {GridCell[][]} grid - Generated grid matrix
     * @param {GridConfig} options - grid config options
     * @param {Scrollbars} scrollbars - scrollbars instance from diagramCamera
     * @returns { void }
     */
    drawVisibleGrid(grid, options, scrollbars) {
        const scrolls = {
            left: scrollbars.left,
            top: scrollbars.top
        };
        for (let i = 0; i < grid.length; i++) {
            for (let x = 0; x < grid[i].length; x++) {
                const cell = grid[i][x];
                // checking is inside the viewport + a cell width / height to include the partially seen elements
                if (this.isCellInViewport(cell, options, scrolls, scrollbars)) {
                    continue;
                }
                cell.draw(null, false);
            }
        }
    }

    /**
     * @description Draws all cell independent plugins that are in the viewport
     * @param {CellIndependentPluginHandler} pluginHandler  - CellIndependentPluginHandler instance
     * @param {Scrollbars} scrollbars - scrollbars instance from diagramCamera
     * @returns { void }
     */
    drawCellIndependentPlugins(pluginHandler, scrollbars) {
        for (const key of pluginHandler.plugins.keys()) {
            const plugin = pluginHandler.plugins.get(key);
            if (this.isElementInViewportV2(plugin.instance.position, plugin.instance.size, { left: scrollbars.left, top: scrollbars.top }, scrollbars)) {
                continue;
            }
            plugin.instance.draw();
        }
    }

    /**
     * @description Returns the camera scale factor
     * @param {Scrollbars} scollbars - Scollbars instance
     * @returns {Number} - scale factor
     */
    getScaleFactorFromScrollbars(scrollbars) {
        return scrollbars.getCamera().getTransformScaleFactor();
    }

    /**
     * @description Checks to see if a cell is in viewport
     * @param {GridCell} cell
     * @param {GridConfig} options
     * @param {Object} scrolls - scrollbar offset
     * @param {Scrollbars} scrollbars  - scrollbars instances
     */
    isCellInViewport(cell, options, scrolls, scrollbars) {
        const scaleFactor = this.getScaleFactorFromScrollbars(scrollbars);
        return cell.layout.x + options.cellWidth < (scrolls.left / scaleFactor) || cell.layout.y + options.cellHeight < (scrolls.top / scaleFactor);
    }

    /**
     * @description Checks to see if a cell is in viewport
     * @param {any} elem - Any element with x, y, width any height properties
     * @param {GridConfig} options
     * @param {Object} scrolls - scrollbar offset
     * @param {Scrollbars} scrollbars  - scrollbars instances
     */
    isElementInViewport(elem, options, scrolls, scrollbars) {
        const scaleFactor = this.getScaleFactorFromScrollbars(scrollbars);
        return elem.x + options.cellWidth < (scrolls.left / scaleFactor) || elem.y + options.cellHeight < (scrolls.top / scaleFactor);
    }

    /**
     * @description Checks to see if a element is in viewport
     * @param {Object} pos  { x,y } indicating elem position
     * @param {Object} size { width, height} indicating elem size
     * @param {Object} viewport {left,top, width,height} - indicating camera offset and visible width and heighht
     * @returns { Boolean }
     */
    isElementInViewportV2(pos, size, viewport) {
        return (pos.x + size.width < viewport.left || pos.x > viewport.width)
            || (pos.y + size.height < viewport.top || pos.y > viewport.height);
    }

    /**
     *
     * @param { GridCell[][] } grid
     * @param {GridConfig} options
     * @param {Scrollbars} scrollbars  - scrollbars instances
     * @param {CanvasRenderingContext2D} canvasCtx  - 2d context
     * @param {LabelHandler} labelHandler  - LabelHandler instance
     * @returns { void }
     */
    drawVisibleInitialGrid(grid, options, scrollbars, canvasCtx, labelHandler) {
        const scrolls = {
            left: scrollbars.left,
            top: scrollbars.top
        };
        const cellsPerRow = Math.floor(options.gridWidth / options.cellWidth);
        const rowsPerPage = Math.floor(options.gridHeight / options.cellHeight);
        let currentY = options.viewPadding;
        for (let row = 0; row < rowsPerPage; row++) {
            if (!grid[row]) grid[row] = [];
            let currentX = options.viewPadding;
            currentY = row * options.cellHeight + options.viewPadding;
            if (currentY === 0) currentY = options.viewPadding;
            for (let cell = 0; cell < cellsPerRow; cell++) {
                // TODO:
                // Calculate radius
                const radius = 0;
                // TODO:
                // Calculate drawstyles
                const cellLayout = {
                    x: currentX, y: currentY, width: options.cellWidth, height: options.cellHeight, radius
                };
                const instance = new GridCell(cellLayout, {}, canvasCtx, labelHandler);
                grid[row][cell] = instance;
                currentX += options.cellWidth;
                if (!this.isCellInViewport(instance, options, scrolls, scrollbars)) {
                    instance.draw();
                }
            }
        }
    }

    /**
     * @descriptino Returns the row and col indexes inside the generated grid matrix
     * @param {GridConfig} config
     * @param {GridCell} instance
     */
    identifyCell(config, instance) {
        const row = Math.floor((instance.y - config.viewPadding) / config.cellHeight);
        const col = Math.floor((instance.x - config.viewPadding) / config.cellWidth);
        return { row, col };
    }

    /**
     * @description Sets and draws the initialization
     * @param {GridCell[][]} grid - Generated grid matrix
     * @param { FabricDiagram } fabricDiagramCtx -  FabricDiagram instance
     * @param { GridConfig } config - GridConfig instance
     * @param { Object } data - diagram object model
     * @param { Scrollbars } scrollbars - Camera scrollbars instance
     * @param { CellIndependentPluginHandler } cellIndependentPluginHandler - CellIndependentPluginHandler instance
     * @param { CanvasRenderingContext2D } ctx - canvas 2d ctx
     * @param { Boolean } readonly - If the diagram is readonly
     * @param { AnchoredPluginsHandler } anchoredPluginsHandler - AnchoredPluginsHandler instance
     * @returns { void }
     */
    setInitialGridContent(grid, fabricDiagramCtx, config, data, scrollbars, cellIndependentPluginHandler, ctx, readonly, anchoredPluginsHandler, isDataModel) {
        const scrolls = {
            left: scrollbars.left,
            top: scrollbars.top
        };
        if (!data || (data && !data.content)) {
            return;
        }
        for (let i = 0; i < data.content.length; i++) {
            const instance = data.content[i];
            if (!instance.hasOwnState) {
                this.drawCellBasedPlugin(grid, instance, fabricDiagramCtx, config, scrolls, anchoredPluginsHandler, ctx);
            } else {
                this.drawCellIndependentPlugin(instance, fabricDiagramCtx, config, scrolls, cellIndependentPluginHandler, ctx, grid, readonly, isDataModel);
            }
        }
    }

    /**
     * @description Draws the canvas background
     * @param {CanvasRenderingContext2D} canvas - canvas 2d ctx
     * @param {FabricDiagram} fabricDiagramCtx  - FabricDiagram instance
     */
    drawBackground(pos, canvas, fabricDiagramCtx) {
        const parentSize = getParentSize(fabricDiagramCtx.config.containerID);
        const adjustedParentSize = { width: parentSize.width * fabricDiagramCtx.diagramCamera.transformScale, height: parentSize.height * fabricDiagramCtx.diagramCamera.transformScale };
        canvas.beginPath();
        canvas.fillStyle = VIEW_COLORS.BG_COLOR;
        canvas.fillRect(0, 0, 10000, 10000);// fabricDiagramCtx.diagramCamera.WIDTH, fabricDiagramCtx.diagramCamera.HEIGHT);
        canvas.closePath();
    }

    /**
     * @description Clears background
     * @param { Object } pos - {width, height}
     * @param { CanvasRenderingContext2D } canvas - 2d ctx canvas
     */
    clearCanvas(pos, canvas) {
        canvas.beginPath();
        canvas.clearRect(0, 0, (pos && pos.width) || canvas.canvas.width, (pos && pos.height) || canvas.canvas.height);
        canvas.closePath();
    }

    /**
     * @description Draws cell based plugins
     * @param { GridCell[][] } grid - generated grid matrix holding gridcell refs
     * @param { Object } instance - plugin instance
     * @param { FabricDiagram } fabricDiagramCtx
     * @param { GridConfig } config - GridConfig instance
     * @param { Object } scrolls - scrollbars offset
     * @param { AnchoredPluginsHandler } anchoredPluginsHandler
     * @param { CanvasRenderingContext2D } ctx
     * @returns { void }
     */
    drawCellBasedPlugin(grid, instance, fabricDiagramCtx, config, scrolls, anchoredPluginsHandler, ctx) {
        const pluginRetrieval = new PluginRetrievalUtils();
        const pluginFactory = pluginRetrieval.getPluginFromDiagram({ pluginId: instance.id }, fabricDiagramCtx);
        const cellPos = this.identifyCell(config, instance);
        const cell = grid[cellPos.row][cellPos.col];
        const contentData = { id: instance.id, uuid: instance.uuid };
        cell.setContent({ factory: pluginFactory.plugin }, contentData);
        if (this.isCellInViewport(cell, config, scrolls, fabricDiagramCtx.diagramCamera.scrollbars));
        cell.draw();
        if (instance.anchoredPlugins && instance.anchoredPlugins.length > 0) {
            this.drawAnchoredPlugins(instance.anchoredPlugins, anchoredPluginsHandler, cell.getContentInstance(), fabricDiagramCtx, ctx, grid);
        }
    }

    /**
     * @description
     * @param { Array } plugins - array of plugins to be drawed
     * @param { AnchoredPluginsHandler} anchoredPluginsHandler  -  anchoredPluginsHandler instance
     * @param { Object } contentInstance - plugin instance
     * @param { FabricDiagram } fabricDiagramCtx - Fabric diagram instance
     * @param { CanvasRenderingContext2D } ctx  - canvas 2d ctx
     * @param {GridCell[][]} grid - generated grid
     * @returns { void }
     */
    drawAnchoredPlugins(plugins, anchoredPluginsHandler, contentInstance, fabricDiagramCtx, ctx, grid) {
        const pluginRetrieval = new PluginRetrievalUtils();
        plugins.forEach(plugin => {
            const target = {
                targetUuid: plugin.anchoredTo, type: "CELL_BASED", targetInstance: contentInstance, position: plugin.data.position, offset: plugin.data.offset, data: plugin.data
            };
            const pluginFactory = pluginRetrieval.getPluginFromDiagram({ pluginId: plugin.data.id }, fabricDiagramCtx);
            anchoredPluginsHandler.addPlugin(target, pluginFactory.plugin, ctx, grid);
        });
    }

    /**
     * @description  Draws cell independent plugins
     * @param { Object } instance - plugin instance
     * @param { FabricDiagram } fabricDiagramCtx - Fabric Diagram instance
     * @param { GridConfig } config -  Gridconfig instance
     * @param { Object } scrolls - {left,top} scrollbars offsets
     * @param { CellIndependentPluginHandler } cellIndependentPluginHandler
     * @param { CanvasRenderingContext2D } ctx
     * @param { GridCell[][] } grid
     * @param { Boolean } readonly
     * @returns { void }
     */
    drawCellIndependentPlugin(instance, fabricDiagramCtx, config, scrolls, cellIndependentPluginHandler, ctx, grid, readonly, isDataModel) {
        const pluginRetrieval = new PluginRetrievalUtils();
        const pluginFactory = pluginRetrieval.getPluginFromDiagram({ pluginId: instance.id }, fabricDiagramCtx);
        const contentData = {
            id: instance.id, uuid: instance.uuid, label: (instance && instance.data && instance.data.label ? instance.data.label : ""), children: instance.children
        };
        const position = { x: instance.x, y: instance.y };
        const size = { width: instance.width, height: instance.height };
        cellIndependentPluginHandler.createPlugin(pluginFactory.plugin, contentData.id, position, size, grid, ctx, contentData, { readonly }, isDataModel);
        if (this.isElementInViewport(instance, config, scrolls, fabricDiagramCtx.diagramCamera.scrollbars)) {
            cellIndependentPluginHandler.plugins.get(contentData.uuid).instance.draw();
        }
    }

    /**
     * @description
     * @param { ConnectingLineHandler } lineHandler -  ConnectingLineHandler instance
     * @param { Object } data - Diagram object model
     * @param { GridCell[][] } grid
     * @param { GridConfig } config
     * @param { LabelHandler } labelHandler
     * @returns { void }
     */
    setInitialGridConnectors(lineHandler, data, grid, config, labelHandler) {
        if (!data || (data && !data.connectors)) {
            return;
        }
        for (let i = 0; i < data.connectors.length; i++) {
            const instance = data.connectors[i];
            const connectingLine = { path: instance.path, side: instance.endSide };
            const fromContent = data.content.find(contentPiece => contentPiece.uuid === instance.from);
            const fromCellPos = this.identifyCell(config, fromContent);
            const fromCellUuid = this.cellRetrieval.getCell(fromCellPos, grid).uuid;
            const toContent = data.content.find(contentPiece => contentPiece.uuid === instance.to);
            const toCellPos = this.identifyCell(config, toContent);
            const toCellUuid = this.cellRetrieval.getCell(toCellPos, grid).uuid;
            lineHandler.createConnection(connectingLine, false, fromCellUuid, toCellUuid, grid, instance, labelHandler, true);
        }
    }
}
